import FlightIcon from '@mui/icons-material/Flight'
import HotelIcon from '@mui/icons-material/Hotel'
import React from 'react'

// ============================================================================
// Types & Interfaces
// ============================================================================

/** User menu item interface */
export interface UserMenuItem {
    label: string
    href: string
}

/** Navigation link interface */
export interface NavLink {
    label: string
    href: string
    labelKey?: string
    icon?: React.ReactNode
    isActive?: boolean
}

/** Header visibility flags interface */
export interface HeaderVisibility {
    showLangCurrDropdown: boolean
    showDeals: boolean
    shouldHideSignIn: boolean
    isOffersPage: boolean
    isItineraryPage: boolean
    isSignInPage: boolean
}

/** User object interface for display name extraction */
export interface User {
    firstName?: string
    displayName?: string
    email?: string
}

// ============================================================================
// Support URL Utilities
// ============================================================================

export const getSupportHref = (locale: string): string => `/${locale}/contact`

export const getContactSupportHref = (locale: string): string => getSupportHref(locale)

// ============================================================================
// User Menu Utilities
// ============================================================================

/** Generates user menu items for header (Flyin) */
export const getUserMenuItems = (t: (key: string) => string, locale: string): UserMenuItem[] => {
    return [
        { label: t('userMenu.myBookings'), href: `/${locale}/trips` },
        { label: t('userMenu.myWallet'), href: `/${locale}/wallet` },
        { label: t('userMenu.profile'), href: `/${locale}/profile` },
        { label: t('userMenu.travellers'), href: `/${locale}/travellers` },
        { label: t('userMenu.myReviews'), href: '/manageReviews' },
        { label: t('userMenu.giveFeedback'), href: '/feedback' }
    ]
}

/** Cleartrip user menu: Trips, Travellers, Profile, Wallet (settings disabled) */
export const getCTUserMenuItems = (t: (key: string) => string, locale: string): UserMenuItem[] => {
    return [
        { label: t('userMenu.trips'), href: `/${locale}/trips` },
        { label: t('userMenu.travellers'), href: `/${locale}/travellers` },
        { label: t('userMenu.profile'), href: `/${locale}/profile` },
        { label: t('userMenu.wallet'), href: `/${locale}/wallet` },
        { label: t('userMenu.expressway'), href: '/accounts/expressway' },
        { label: t('userMenu.settings'), href: `/account/settings` }
    ]
}

/** Gets user display name with fallback logic (firstName → displayName → email username → 'User') */
export const getUserDisplayName = (user: User): string => {
    if (user.firstName) {
        return user.firstName
    }

    if (user.displayName) {
        return user.displayName
    }

    if (user.email) {
        return user.email.split('@')[0]
    }

    return 'User'
}

// ============================================================================
// Navigation Utilities
// ============================================================================

/** Generates navigation links for Flyin header variant */
export const getFlyinNavLinks = (locale: string, showVisa: boolean = false): NavLink[] => {
    const localePrefix = `/${locale}`

    return [
        { label: 'Flights', href: `${localePrefix}/flights` },
        { label: 'Hotels', href: `${localePrefix}/hotels` },
        showVisa
            ? { label: 'visa', href: '/visa' }
            : { label: 'fph', href: `${localePrefix}/flights-hotels` }
    ]
}

/** Generates navigation links for Cleartrip header variant with active state (Visa href has no locale).
 * Visa link is only shown when showVisa is true (Cleartrip AE domain only). */
export const getCTNavLinks = (
    locale: string,
    activeVertical: string | null,
    showVisa: boolean = false
): NavLink[] => {
    const localePrefix = `/${locale}`

    const links: NavLink[] = [
        {
            label: 'Flights',
            href: `${localePrefix}/flights`,
            isActive: activeVertical === 'flights'
        },
        {
            label: 'Hotels',
            href: '/hotels',
            isActive: activeVertical === 'hotels'
        }
    ]
    if (showVisa) {
        links.push({
            label: 'Visa',
            href: '/visa',
            isActive: activeVertical === 'visa'
        })
    }
    return links
}

/** Gets icon component for navigation item label (case-insensitive) */
export const getNavIcon = (label: string): React.ReactNode | null => {
    const lowerLabel = label.toLowerCase()
    if (lowerLabel === 'flights') {
        return React.createElement(FlightIcon, { sx: { fontSize: 20, color: 'inherit' } })
    }
    if (lowerLabel === 'hotels') {
        return React.createElement(HotelIcon, { sx: { fontSize: 20, color: 'inherit' } })
    }
    return null
}

/** Transforms label to translation key for Flyin header navigation links */
export const getTranslationKey = (label: string, labelKey?: string): string => {
    if (labelKey) {
        return labelKey
    }

    return label
        .replace(/ \+ /g, 'Plus')
        .replace(/ /g, '')
        .replace(/^[A-Z]/, c => c.toLowerCase())
}

// ============================================================================
// Visibility Utilities
// ============================================================================

/** Determines visibility flags for header elements based on current pathname and variant */
export const getHeaderVisibility = (
    pathname: string,
    variant: 'flyin' | 'cleartrip' = 'flyin'
): HeaderVisibility => {
    const matchers = {
        homepage: (path: string) => path === '/' || /^\/[a-z]{2}$/.test(path),
        flights: (path: string) => path === '/flights' || /^\/[a-z]{2}\/flights\/?$/.test(path),
        srp: (path: string) => path === '/flights/results' || /^\/[a-z]{2}\/flights\/results/.test(path),
        offers: (path: string) => path.includes('/offers'),
        airlines: (path: string) => path.includes('/airlines'),
        itinerary: (path: string) => path.includes('/flights/itinerary'),
        signin: (path: string) => path.includes('/signin'),
        flightSchedule: (path: string) => path.includes('/flight-schedule')
    }

    const pageFlags = {
        homepage: matchers.homepage(pathname),
        flights: matchers.flights(pathname),
        srp: matchers.srp(pathname),
        offers: matchers.offers(pathname),
        airlines: matchers.airlines(pathname),
        itinerary: matchers.itinerary(pathname),
        signin: matchers.signin(pathname),
        flightSchedule: matchers.flightSchedule(pathname)
    }

    const visibleByVariant = {
        showLangCurrDropdown: {
            flyin: ['homepage', 'flights', 'srp', 'offers', 'flightSchedule'],
            cleartrip: ['homepage', 'srp']
        },
        showDeals: {
            flyin: ['homepage', 'flights', 'srp', 'airlines'],
            cleartrip: ['homepage', 'srp', 'airlines']
        }
    } as const

    const isInGroup = (group: readonly string[]) => group.some(key => pageFlags[key as keyof typeof pageFlags])

    const showLangCurrDropdown = isInGroup(visibleByVariant.showLangCurrDropdown[variant])
    const showDeals = isInGroup(visibleByVariant.showDeals[variant])
    const shouldHideSignIn = pageFlags.itinerary || pageFlags.signin || pageFlags.offers

    return {
        showLangCurrDropdown,
        showDeals,
        shouldHideSignIn,
        isOffersPage: pageFlags.offers,
        isItineraryPage: pageFlags.itinerary,
        isSignInPage: pageFlags.signin
    }
}
